import { apiClient } from './api';
import {
  MembershipPlan,
  Subscription,
  CashPaymentRecord,
  CreditWallet,
  MembershipMetrics,
  PlanPerformanceMetrics,
  UsageAnalytics,
  RevenueAnalytics,
  CustomerRetentionAnalytics,
  MembershipAnalyticsFilters,
  CreateMembershipPlanRequest,
  CreateSubscriptionRequest,
  RecordCashPaymentRequest,
} from '../types/membership';

class MembershipService {
  // Membership Plans
  async getMembershipPlans(params?: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    isActive?: boolean;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

    const query = queryParams.toString();
    return apiClient.get<{
      plans: MembershipPlan[];
      total: number;
      page: number;
      totalPages: number;
    }>(`/membership/plans${query ? `?${query}` : ''}`);
  }

  async getMembershipPlan(id: string) {
    return apiClient.get<MembershipPlan>(`/membership/plans/${id}`);
  }

  async createMembershipPlan(data: CreateMembershipPlanRequest) {
    return apiClient.post<MembershipPlan>('/membership/plans', data);
  }

  async updateMembershipPlan(id: string, data: Partial<CreateMembershipPlanRequest>) {
    return apiClient.put<MembershipPlan>(`/membership/plans/${id}`, data);
  }

  async deleteMembershipPlan(id: string) {
    return apiClient.delete(`/membership/plans/${id}`);
  }

  async activateMembershipPlan(id: string) {
    return apiClient.patch<MembershipPlan>(`/membership/plans/${id}/activate`);
  }

  async deactivateMembershipPlan(id: string) {
    return apiClient.patch<MembershipPlan>(`/membership/plans/${id}/deactivate`);
  }

  // Subscriptions
  async getSubscriptions(params?: {
    page?: number;
    limit?: number;
    customerId?: string;
    planId?: string;
    status?: string;
    search?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.customerId) queryParams.append('customerId', params.customerId);
    if (params?.planId) queryParams.append('planId', params.planId);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    return apiClient.get<{
      subscriptions: Subscription[];
      total: number;
      page: number;
      totalPages: number;
    }>(`/subscriptions${query ? `?${query}` : ''}`);
  }

  async getSubscription(id: string) {
    return apiClient.get<Subscription>(`/subscriptions/${id}`);
  }

  async createSubscription(data: CreateSubscriptionRequest) {
    return apiClient.post<Subscription>('/subscriptions', data);
  }

  async activateSubscription(id: string) {
    return apiClient.patch<Subscription>(`/subscriptions/${id}/activate`);
  }

  async renewSubscription(id: string, data: { endDate: string }) {
    return apiClient.patch<Subscription>(`/subscriptions/${id}/renew`, data);
  }

  async upgradeSubscription(id: string, data: { newPlanId: string; endDate: string }) {
    return apiClient.patch<Subscription>(`/subscriptions/${id}/upgrade`, data);
  }

  async pauseSubscription(id: string, data: { reason?: string }) {
    return apiClient.patch<Subscription>(`/subscriptions/${id}/pause`, data);
  }

  async resumeSubscription(id: string) {
    return apiClient.patch<Subscription>(`/subscriptions/${id}/resume`);
  }

  async cancelSubscription(id: string, data: { reason?: string }) {
    return apiClient.patch<Subscription>(`/subscriptions/${id}/cancel`, data);
  }

  async extendSubscription(id: string, data: { days: number; reason?: string }) {
    return apiClient.patch<Subscription>(`/subscriptions/${id}/extend`, data);
  }

  // Cash Payments
  async recordCashPayment(data: RecordCashPaymentRequest) {
    return apiClient.post<CashPaymentRecord>('/cash-payments', data);
  }

  async getCashPayments(params?: {
    page?: number;
    limit?: number;
    subscriptionId?: string;
    startDate?: string;
    endDate?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.subscriptionId) queryParams.append('subscriptionId', params.subscriptionId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const query = queryParams.toString();
    return apiClient.get<{
      payments: CashPaymentRecord[];
      total: number;
      page: number;
      totalPages: number;
    }>(`/cash-payments${query ? `?${query}` : ''}`);
  }

  async getCashPayment(id: string) {
    return apiClient.get<CashPaymentRecord>(`/cash-payments/${id}`);
  }

  // Credits
  async getCustomerCredits(customerId: string) {
    return apiClient.get<CreditWallet>(`/credits/customer/${customerId}`);
  }

  async addCredits(data: {
    customerId: string;
    subscriptionId: string;
    credits: number;
    reason?: string;
  }) {
    return apiClient.post<CreditWallet>('/credits/add', data);
  }

  async deductCredits(data: {
    customerId: string;
    credits: number;
    serviceId: string;
    reason?: string;
  }) {
    return apiClient.post<CreditWallet>('/credits/deduct', data);
  }

  // Analytics
  async getMembershipMetrics(filters?: MembershipAnalyticsFilters) {
    const queryParams = new URLSearchParams();
    if (filters?.startDate) queryParams.append('startDate', filters.startDate);
    if (filters?.endDate) queryParams.append('endDate', filters.endDate);
    if (filters?.planType) queryParams.append('planType', filters.planType);
    if (filters?.status) queryParams.append('status', filters.status);

    const query = queryParams.toString();
    return apiClient.get<MembershipMetrics>(
      `/membership-analytics/metrics${query ? `?${query}` : ''}`
    );
  }

  async getPlanPerformanceMetrics(filters?: MembershipAnalyticsFilters) {
    const queryParams = new URLSearchParams();
    if (filters?.startDate) queryParams.append('startDate', filters.startDate);
    if (filters?.endDate) queryParams.append('endDate', filters.endDate);

    const query = queryParams.toString();
    return apiClient.get<PlanPerformanceMetrics[]>(
      `/membership-analytics/plan-performance${query ? `?${query}` : ''}`
    );
  }

  async getUsageAnalytics(filters?: MembershipAnalyticsFilters) {
    const queryParams = new URLSearchParams();
    if (filters?.startDate) queryParams.append('startDate', filters.startDate);
    if (filters?.endDate) queryParams.append('endDate', filters.endDate);

    const query = queryParams.toString();
    return apiClient.get<UsageAnalytics>(`/membership-analytics/usage${query ? `?${query}` : ''}`);
  }

  async getRevenueAnalytics(filters?: MembershipAnalyticsFilters) {
    const queryParams = new URLSearchParams();
    if (filters?.startDate) queryParams.append('startDate', filters.startDate);
    if (filters?.endDate) queryParams.append('endDate', filters.endDate);

    const query = queryParams.toString();
    return apiClient.get<RevenueAnalytics>(
      `/membership-analytics/revenue${query ? `?${query}` : ''}`
    );
  }

  async getCustomerRetentionAnalytics() {
    return apiClient.get<CustomerRetentionAnalytics>('/membership-analytics/retention');
  }

  async generateMembershipReport(filters?: MembershipAnalyticsFilters) {
    const queryParams = new URLSearchParams();
    if (filters?.startDate) queryParams.append('startDate', filters.startDate);
    if (filters?.endDate) queryParams.append('endDate', filters.endDate);
    if (filters?.planType) queryParams.append('planType', filters.planType);
    if (filters?.status) queryParams.append('status', filters.status);

    const query = queryParams.toString();
    return apiClient.get<{
      metrics: MembershipMetrics;
      planPerformance: PlanPerformanceMetrics[];
      usageAnalytics: UsageAnalytics;
      revenueAnalytics: RevenueAnalytics;
      retentionAnalytics: CustomerRetentionAnalytics;
      generatedAt: string;
    }>(`/membership-analytics/report${query ? `?${query}` : ''}`);
  }
}

export const membershipService = new MembershipService();
